// Global keyboard listener for iOS Capacitor. Call once in Layout.tsx.
//
// Why this exists: with `resize: 'body'`, Capacitor shrinks the webview
// to sit above the keyboard, BUT it does NOT auto-scroll the focused
// input into view, and our fixed-position tab bar will float over the
// keyboard unless we hide it. We also expose `--keyboard-height` as a
// CSS custom property so any sticky bottom CTA can lift with it.

import type React from 'react';
import { useEffect } from 'react';
import { isNative } from './native';

// Input types that DON'T summon the iOS keyboard - they show a wheel
// picker or modal sheet instead. We must NOT auto-scroll for these,
// because the scroll shifts the input element's position and iOS
// dismisses the picker the moment its trigger element moves. The
// History page's "Jump" date picker hit this exact bug.
const NON_KEYBOARD_INPUT_TYPES = new Set([
  'date',
  'datetime-local',
  'time',
  'month',
  'week',
  'color',
  'file',
  'checkbox',
  'radio',
  'range',
  'submit',
  'reset',
  'button',
  'image',
  'hidden',
]);

function isKeyboardSummoningInput(el: HTMLElement | null): boolean {
  if (!el) return false;
  if (el.tagName === 'TEXTAREA') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type.toLowerCase();
  return !NON_KEYBOARD_INPUT_TYPES.has(type);
}

// Scroll a focused input above the on-screen keyboard.
//
// Why this exists: `scrollIntoView({ block: 'center' })` centers against
// `window.innerHeight`, which on iOS Capacitor `resize: body` stays at the
// full viewport height (~844px) even when the keyboard occupies the bottom
// ~335px. "Center" therefore = mid-full-screen = under the keyboard. We
// need the center of the *visible* area (innerHeight - keyboardHeight).
//
// useKeyboard writes `--keyboard-height` to <html> on keyboardWillShow, so
// we read it back here and compute the scroll delta explicitly. When the
// keyboard is closed (or on web/non-native where no height is set), we
// fall back to a plain center-scroll which browsers handle natively.
//
// Skips date / time / color / file / checkbox / etc. inputs - those don't
// summon a keyboard, and scrolling them dismisses their picker on iOS.
export function scrollInputAboveKeyboard(target: HTMLElement): void {
  if (!isKeyboardSummoningInput(target)) return;
  const kbRaw = getComputedStyle(document.documentElement)
    .getPropertyValue('--keyboard-height')
    .trim();
  const kbHeight = parseInt(kbRaw, 10) || 0;

  // Find the nearest scrollable ancestor. If the input lives inside a
  // modal bottom-sheet (.modal-sheet), we must scroll *that* container
  // rather than the window, because the modal is position:fixed and
  // window.scrollBy is a no-op for its contents.
  const scrollContainer = target.closest('.modal-sheet') as HTMLElement | null;

  if (kbHeight <= 0) {
    try {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch {
      target.scrollIntoView();
    }
    return;
  }

  const rect = target.getBoundingClientRect();
  const visibleHeight = window.innerHeight - kbHeight;
  const buffer = 32; // gap between input bottom and keyboard top

  // Already clear of the keyboard and above the top edge - leave it alone.
  if (rect.top >= 16 && rect.bottom <= visibleHeight - buffer) return;

  // Place the input ~25% down the visible area, clamped so it never hugs
  // the very top. Negative delta scrolls up, positive scrolls down.
  const desiredTop = Math.max(96, Math.round(visibleHeight * 0.25));
  const delta = rect.top - desiredTop;

  if (scrollContainer) {
    scrollContainer.scrollBy({ top: delta, behavior: 'smooth' });
  } else {
    window.scrollBy({ top: delta, behavior: 'smooth' });
  }
}

// Shared form focus handler: when a text input receives focus inside a form,
// scroll it above the keyboard after the keyboard animates in. Covers the
// "tap between fields while keyboard is already open" case, which iOS
// WebView does not auto-scroll reliably and which `useKeyboard` can't
// catch (no keyboardWillShow fires - the keyboard was already up).
export function scrollFocusedInputIntoView(
  event: React.FocusEvent<HTMLFormElement>,
): void {
  const target = event.target as HTMLElement | null;
  if (!target) return;
  const tag = target.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
  // Same guard as scrollInputAboveKeyboard - skip date / time / color /
  // file / checkbox / etc. inputs whose UI is a picker, not a keyboard.
  if (tag === 'INPUT' && !isKeyboardSummoningInput(target)) return;
  // 300ms gives iOS time to finish its keyboard animation (~250-330ms)
  // and lets useKeyboard write the final --keyboard-height before we read
  // it. Firing earlier reads 0 and falls back to plain center-scroll.
  window.setTimeout(() => {
    if (document.activeElement !== target) return;
    scrollInputAboveKeyboard(target);
  }, 300);
}

export function useKeyboard(): void {
  useEffect(() => {
    if (!isNative()) return;

    let cleanup: (() => void) | undefined;

    // Dynamic import so the plugin never loads on the web build.
    (async () => {
      try {
        const { Keyboard } = await import('@capacitor/keyboard');
        const html = document.documentElement;

        // Make sure the WebView scroll stays ENABLED while the keyboard
        // is open. iOS WKWebView can disable scroll when a form control
        // is focused, trapping users who can't reach other fields on
        // the page without dismissing the keyboard first.
        try {
          await Keyboard.setScroll({ isDisabled: false });
        } catch {
          // Older plugin versions may not support this - non-fatal.
        }

        // Show the iOS keyboard accessory bar (Previous / Next / Done
        // buttons). Critical for numeric keypads, which have NO native
        // dismiss affordance otherwise - users get trapped tapping
        // random empty space to close the keyboard.
        try {
          await Keyboard.setAccessoryBarVisible({ isVisible: true });
        } catch {
          // Older plugin versions - non-fatal.
        }

        const willShow = await Keyboard.addListener('keyboardWillShow', (info) => {
          html.dataset.keyboardOpen = 'true';
          html.style.setProperty('--keyboard-height', `${info.keyboardHeight}px`);
          // Re-assert scroll enabled each time the keyboard shows, in
          // case something else toggled it.
          Keyboard.setScroll({ isDisabled: false }).catch(() => {});
          // Let the webview finish shrinking, then scroll the focused
          // input above the keyboard. Capacitor doesn't auto-scroll for
          // us with resize: 'body'. `scrollInputAboveKeyboard` does the
          // right math against --keyboard-height instead of assuming
          // window.innerHeight is the visible area.
          requestAnimationFrame(() => {
            const active = document.activeElement as HTMLElement | null;
            if (active) scrollInputAboveKeyboard(active);
          });
        });

        const willHide = await Keyboard.addListener('keyboardWillHide', () => {
          delete html.dataset.keyboardOpen;
          html.style.setProperty('--keyboard-height', '0px');
        });

        cleanup = () => {
          willShow.remove();
          willHide.remove();
        };
      } catch (err) {
        console.warn('[useKeyboard] plugin load failed, falling back to no-op', err);
      }
    })();

    return () => {
      cleanup?.();
    };
  }, []);
}
